import { ActionEvents } from '@griply/types';
import {
  computeHabitEntryUpdate,
  taskTypeToTrackingTaskType,
} from '@griply/utils';
import { useCallback } from 'react';
import { useOpenTaskDetail } from 'shared/contexts/task-detail';
import { useToday } from 'shared/contexts/today';
import { useUpdateTasksMutation } from 'shared/hooks/queries/use-update-tasks-mutation';
import { useTrackEvents } from 'shared/hooks/use-track-events';
import { Habit } from 'shared/types/habit';
import { ScheduleEntryState } from 'shared/types/habit-schedule';
import { RepeatingTask } from 'shared/types/repeating-task';
import { Timestamp } from 'shared/types/timestamp';

export type Options = {
  value?: number;
  increment?: number;
  toggle?: boolean;
  keepDetailOpen?: boolean;
};

export const useUpdateHabitScheduleEntry = () => {
  const today = useToday();
  const { mutate: updateTasks } = useUpdateTasksMutation();
  const track = useTrackEvents();
  const openTaskDetail = useOpenTaskDetail();

  return useCallback(
    (
      habit: Habit | RepeatingTask,
      date: Timestamp = new Date(),
      type: ScheduleEntryState = ScheduleEntryState.Complete,
      { value, increment, toggle, keepDetailOpen }: Options = {},
    ) => {
      const { newSchedules, shouldCompleteHabit, ignored } =
        computeHabitEntryUpdate(habit, date, {
          today,
          type,
          value,
          increment,
          toggle,
        });

      if (ignored) {
        return;
      }

      if (shouldCompleteHabit) {
        track(ActionEvents.TaskComplete, {
          type: taskTypeToTrackingTaskType[habit.type],
        });
      }

      // Only the habit is written here. A childIds reset would share this
      // atomic batch, letting an un-writable subtask roll back the schedule
      // edit. Subtask resets belong to the completion flow (today-controls).
      updateTasks([{ id: habit.id, schedules: newSchedules }]);

      !keepDetailOpen && shouldCompleteHabit && openTaskDetail(undefined);
    },
    [openTaskDetail, today, track, updateTasks],
  );
};
